import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { AssetDto, CommentPosition, GalleryDto } from '@nusign/picflow-backend-types';
import { Observable, map } from 'rxjs';
import { Rect } from '../../../classes/rect.class';
import { debounce } from '../../../lib/debounce';
import { GetBounds } from '../../../lib/get-bounds';
import { getCommentPositionCenter } from '../../../lib/get-comment-position-center';
import { CanShowFacePipe } from '../../../pipes/can-show-face';
import { IsArrayPipe } from '../../../pipes/is-array.pipe';
import { IsCurrentUserPipe } from '../../../pipes/is-current-user.pipe';
import { VideoTimeIsInRangePipe } from '../../../pipes/video-time-is-in-range.pipe';
import { CommentService } from '../../../services/comment.service';
import { StateService } from '../../../services/state.service';
import { UpdateService } from '../../../services/update.service';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { CommentThread } from '../comments/comments.component';
import { VideoTimeData } from '../video-viewer/video-viewer.component';

interface CommentThreadFace extends CommentThread {
  displayPosition: Rect;
}

@Component({
  selector: 'app-asset-comment-faces',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './asset-comment-faces.component.html',
  styleUrl: './asset-comment-faces.component.scss',
  imports: [
    AsyncPipe,
    AvatarComponent,
    SvgComponent,
    IsArrayPipe,
    VideoTimeIsInRangePipe,
    IsCurrentUserPipe,
    CanShowFacePipe,
  ],
})
export class AssetCommentFacesComponent implements OnChanges {
  private commentService = inject(CommentService);
  private updateService = inject(UpdateService);
  private state = inject(StateService);

  @Input() asset: AssetDto;
  @Input() gallery: GalleryDto;
  @Input() scale: number;
  @Input() offsetScale: number;
  @Input() offsetX: number;
  @Input() offsetY: number;
  @Input() sideMenuOpen: boolean;
  @Input() isDesktop: boolean;
  @Input() selectedThreadId: string;
  @Input() hoverThreadId: string;
  @Input() transitionPosition: boolean;
  @Input() showResolved: boolean;
  @Input() videoTime: VideoTimeData;
  @Input({ required: true }) galleryPermissions: GalleryDto['permissions'];
  @Input() isTeamMember: boolean;
  @Input() containerOffset: number;
  @Input() pdfMode: boolean;
  @Input() activePage: number;
  @Output() selectThread = new EventEmitter<CommentThread>();
  @Output() threadHover = new EventEmitter<CommentThread>();

  @HostListener('window:resize', ['$event']) async winResize() {
    this.calcBounds();
  }

  commentFace$: Observable<CommentThreadFace[]>;

  potentiallyDragging = signal<CommentThreadFace>(undefined);
  dragging = signal<CommentThreadFace>(undefined);
  mouseDownPosition: Rect;

  bounds = signal<Rect>(undefined);

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.offsetScale ||
      changes.scale ||
      changes.sideMenuOpen ||
      changes.asset ||
      changes.offsetX ||
      changes.offsetY ||
      changes.isDesktop
    ) {
      this.calcBounds();
    }

    if (changes.asset) {
      if (changes.asset) {
        this.updateService.subscribeAsset(this.asset.id);
      }
      this.commentFace$ = this.commentService.getComments(this.asset.id, this.gallery.id).pipe(
        map(comments =>
          this.commentService.filterCommentsForAccessLevel(comments, this.galleryPermissions, this.isTeamMember)
        ),
        map((comments: CommentThreadFace[]) => {
          const threads = comments.filter(comment => !comment.parent_comment) as CommentThreadFace[];
          return threads.filter(thread => thread.position?.x);
        }),
        map(threads => {
          return threads.map(thread => {
            const displayPosition = getCommentPositionCenter(thread.position);
            return { ...thread, displayPosition };
          });
        })
      );
    }
  }

  private calcBounds() {
    this.bounds.set(
      GetBounds(
        this.offsetScale,
        this.scale,
        this.sideMenuOpen,
        this.pdfMode,
        this.asset,
        this.offsetX,
        this.offsetY,
        this.isDesktop
      )
    );
  }

  private allowDragging(thread: CommentThreadFace) {
    if (!this.galleryPermissions.can_edit) return true;
    const userLike = thread.user || thread.guest;
    const currentUser = this.state.user || this.state.guest;
    return userLike?.id === currentUser?.id;
  }

  mouseDown(thread: CommentThreadFace, event: MouseEvent) {
    if (!this.allowDragging(thread)) return;
    this.potentiallyDragging.set(thread);
    this.mouseDownPosition = Rect.fromMouseEvent(event);
    event.stopPropagation();
  }

  mouseMove(event: MouseEvent) {
    event.stopPropagation();
    if (this.potentiallyDragging()) {
      const mousePosition = Rect.fromMouseEvent(event);
      const distance = mousePosition.subtract(this.mouseDownPosition).magnitude();

      if (distance > 7) {
        this.dragging.set(this.potentiallyDragging());
        this.potentiallyDragging.set(undefined);
      }
    }

    if (!this.dragging()) return;
    const movement = new Rect(event.movementX / this.bounds().width, event.movementY / this.bounds().height);
    this.dragging().displayPosition = this.dragging().displayPosition.add(movement);
    event.stopPropagation();
  }

  mouseUp = debounce((event: MouseEvent) => {
    event.stopPropagation();
    this.potentiallyDragging.set(undefined);
    if (!this.dragging()) return;

    this.commentService
      .updateComment(
        this.asset.id,
        this.dragging().id,
        {
          position: this.getNewPosition(),
        },
        this.gallery.id
      )
      .subscribe();

    this.dragging.set(undefined);
    this.potentiallyDragging.set(undefined);
  }, 10);

  click(event: MouseEvent, thread: CommentThreadFace) {
    event.stopPropagation();
    if (this.dragging()) return;
    this.selectThread.emit(thread);
  }

  private getNewPosition(): CommentPosition {
    const thread = this.dragging();

    if (Array.isArray(thread.position.x)) {
      const width = thread.position.x[1] - thread.position.x[0];
      const height = thread.position.y[1] - thread.position.y[0];
      const x = thread.displayPosition.x - width / 2;
      const y = thread.displayPosition.y - height / 2;

      return {
        ...thread.position,
        x: [x, x + width] as const,
        y: [y, y + height] as const,
      };
    }
    const x = thread.displayPosition.x;
    const y = thread.displayPosition.y;

    return {
      ...thread.position,
      x,
      y,
    };
  }
}
